import { Component, ViewChild, AfterViewInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { B2BPlantNavService } from '../../plants/services/B2B-plant-nav.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CulturalPipe } from '../../../pipes/cultural.pipe';

@Component({
    selector: 'app-plant-list-table',
    imports: [MatButtonModule, MatCardModule, MatMenuModule, MatPaginatorModule, MatTableModule, MatCheckboxModule, MatTooltipModule, TranslatePipe, CulturalPipe],
    templateUrl: './plant-list-table.html',
    styleUrl: './plant-list-table.scss'
})
export class PlantListTable implements AfterViewInit {
    displayedColumns: string[] = ['plantName', 'serialNumber', 'production', 'consumption', 'batteryLevel', 'startupDate', 'status', 'action'];
    dataSource = new MatTableDataSource<PeriodicElement>(ELEMENT_DATA);
    selection = new SelectionModel<PeriodicElement>(true, []);

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(
        public themeService: CustomizerSettingsService,
        private b2bPlantNavService: B2BPlantNavService,
        private translate: TranslateService
    ) {
        this.dataSource.filterPredicate = (data: PeriodicElement, filter: string): boolean => {
            const searchableValue = [
                data.serialNumber,
                data.plantName.name,
                data.plantName.city,
                data.status.online,
                data.status.abnormal,
                data.status.offline
            ].filter(Boolean).join(' ').toLowerCase();

            return searchableValue.includes(filter);
        };
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
    }

    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    checkboxLabel(row?: PeriodicElement): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.serialNumber}`;
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    viewPlant(element: PeriodicElement): void {
        this.b2bPlantNavService.goToPlantDetails(element.plantId);
    }

    getStatusType(element: PeriodicElement): 'online' | 'abnormal' | 'offline' {
        if (element.status.online) return 'online';
        if (element.status.abnormal) return 'abnormal';
        return 'offline';
    }

    getStatusLabel(element: PeriodicElement): string {
        return this.translate.instant(this.statusTranslationKey(this.getStatusType(element)));
    }

    private statusTranslationKey(status: 'online' | 'abnormal' | 'offline'): string {
        if (status === 'online') return 'Online';
        if (status === 'abnormal') return 'Abnormal';
        return 'Offline';
    }

    getBatteryPercent(element: PeriodicElement): number | null {
        const value = Number(element.batteryLevel);
        return Number.isFinite(value) ? value : null;
    }

    getBatteryWidth(element: PeriodicElement): number {
        return this.getBatteryPercent(element) ?? 0;
    }
}


const ELEMENT_DATA: PeriodicElement[] = [
    {
        plantId: 1,
        serialNumber: 'TPJ4CCU013',
        plantName: { name: 'TPJ4C', city: 'Aarhus' },
        production: 10.6,
        consumption: 19.2,
        startupDate: '2025-06-14',
        batteryLevel: '54',
        status: { online: 'Online' },
        action: { view: 'visibility' }
    },
    {
        plantId: 2,
        serialNumber: 'GZD191204F',
        plantName: { name: 'GZD19', city: 'Thisted' },
        production: 13.02,
        consumption: 32.04,
        startupDate: '2024-11-02',
        batteryLevel: '-',
        status: { online: 'Online' },
        action: { view: 'visibility' }
    },
    {
        plantId: 3,
        serialNumber: 'HAD1850037',
        plantName: { name: 'HAD18', city: 'Roskilde' },
        production: 0.0,
        consumption: 25.6,
        startupDate: '2025-01-27',
        batteryLevel: '-',
        status: { abnormal: 'Abnormal' },
        action: { view: 'visibility' }
    },
    {
        plantId: 4,
        serialNumber: 'LQD092103A',
        plantName: { name: 'LQD09', city: 'Odense' },
        production: 21.7,
        consumption: 29.51,
        startupDate: '2025-03-06',
        batteryLevel: '100',
        status: { online: 'Online' },
        action: { view: 'visibility' }
    },
    {
        plantId: 5,
        serialNumber: 'MDD0921101',
        plantName: { name: 'MDD09', city: 'Esbjerg' },
        production: 0.0,
        consumption: 21.2,
        startupDate: '2023-12-18',
        batteryLevel: '0',
        status: { offline: 'Offline' },
        action: { view: 'visibility' }
    },
];

export interface PeriodicElement {
    plantId: number;
    serialNumber: string;
    plantName: {
        name: string;
        city: string;
    };
    production: number;
    consumption: number;
    batteryLevel: string;
    startupDate: string;
    status: {
        online?: string;
        abnormal?: string;
        offline?: string;
    };
    action: {
        view: string;
    };
}
